import { Directive, Input, OnInit, OnDestroy, TemplateRef, ViewContainerRef, Renderer2, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { BlockUIService } from './block-ui.service';

/**
 * Structural directive: *blockUI="'block-name'"
 * Renders the host template and overlays a spinner + optional message while the named
 * block is active. Angular 22-native (no ComponentFactoryResolver). Drop-in for ng-block-ui.
 */
@Directive({
  selector: '[blockUI]',
  standalone: false,
})
export class BlockUIDirective implements OnInit, OnDestroy {
  @Input('blockUI') blockName!: string;

  private sub?: Subscription;
  private overlayEl?: HTMLElement;

  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly vcRef: ViewContainerRef,
    private readonly renderer: Renderer2,
    private readonly blockUIService: BlockUIService
  ) {}

  ngOnInit(): void {
    // Always render the wrapped content.
    const viewRef = this.vcRef.createEmbeddedView(this.templateRef);
    this.sub = this.blockUIService.observe(this.blockName).subscribe(({ active, message }) => {
      if (active) {
        this.showOverlay(viewRef, message);
      } else {
        this.hideOverlay();
      }
    });
  }

  private rootNode(viewRef: { rootNodes: unknown[] }): HTMLElement | null {
    const node = (viewRef.rootNodes || []).find((n) => n && (n as Node).nodeType === 1) as HTMLElement | undefined;
    return node ?? null;
  }

  private showOverlay(viewRef: { rootNodes: unknown[] }, message?: string): void {
    if (this.overlayEl) { return; }
    const host = this.rootNode(viewRef);
    if (!host) { return; }
    // Ensure the host can anchor an absolutely-positioned overlay.
    if (getComputedStyle(host).position === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }
    const overlay = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(overlay, 'block-ui-overlay');
    this.renderer.setStyle(overlay, 'position', 'absolute');
    this.renderer.setStyle(overlay, 'inset', '0');
    this.renderer.setStyle(overlay, 'display', 'flex');
    this.renderer.setStyle(overlay, 'align-items', 'center');
    this.renderer.setStyle(overlay, 'justify-content', 'center');
    this.renderer.setStyle(overlay, 'flex-direction', 'column');
    this.renderer.setStyle(overlay, 'background', 'rgba(255,255,255,0.6)');
    this.renderer.setStyle(overlay, 'z-index', '1000');

    const spinner = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(spinner, 'block-ui-spinner');
    // Inline keyframe-free spinner (border animation via class defined in global styles, fallback text).
    this.renderer.setStyle(spinner, 'width', '40px');
    this.renderer.setStyle(spinner, 'height', '40px');
    this.renderer.setStyle(spinner, 'border', '4px solid #ccc');
    this.renderer.setStyle(spinner, 'border-top-color', '#3f51b5');
    this.renderer.setStyle(spinner, 'border-radius', '50%');
    this.renderer.setStyle(spinner, 'animation', 'block-ui-spin 0.8s linear infinite');
    this.renderer.appendChild(overlay, spinner);

    if (message) {
      const msg = this.renderer.createElement('div') as HTMLElement;
      this.renderer.setStyle(msg, 'margin-top', '10px');
      this.renderer.setProperty(msg, 'textContent', message);
      this.renderer.appendChild(overlay, msg);
    }

    this.renderer.appendChild(host, overlay);
    this.overlayEl = overlay;
  }

  private hideOverlay(): void {
    if (this.overlayEl && this.overlayEl.parentNode) {
      this.renderer.removeChild(this.overlayEl.parentNode, this.overlayEl);
    }
    this.overlayEl = undefined;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.hideOverlay();
  }
}
